import { css } from '@emotion/react'
import styled from '@emotion/styled'

import { Theme } from 'scripts/react-components/themes/Theme'

const pageStyles = ({ theme }: Theme) => css`
  label: cashback__page;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacings.giga};
  padding-left: 10px;
  padding-right: 10px;
  color: ${theme.colors.n900};
`
export const Page = styled('div')(pageStyles)

const wrapperStyles = ({ theme }: Theme) => css`
  label: layout-wrapper;
  max-width: 944px;
  background-color: ${theme.colors.white};
  padding: ${theme.spacings.giga} ${theme.spacings.mega};

  ${theme.media.mega} {
    border-radius: ${theme.borderRadius.giga};
    margin-bottom: ${theme.spacings.tera};
    padding-left: ${theme.spacings.giga};
    padding-right: ${theme.spacings.giga};
  }

  @media (min-width: 961px) {
    min-height: 470px;
    border: 1px solid ${theme.colors.n100};
    border-radius: ${theme.borderRadius.giga};
    margin: 0 0 ${theme.spacings.tera} 0;
  }
`

export const Wrapper = styled('div')(wrapperStyles)

const headerStyles = ({ theme }: Theme) => css`
  label: cashback__header;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacings.byte};
`
export const Header = styled('div')(headerStyles)

const titleStyles = ({ theme }: Theme) => css`
  label: cashback__title;
  font-size: 26px;
  font-weight: ${theme.font.weight.bold};
`
export const Title = styled('h1')(titleStyles)

const subtitleStyles = ({ theme }: Theme) => css`
  label: cashback__subtitle;
  color: ${theme.colors.n600};
  font-size: 16px;
`
export const Subtitle = styled('p')(subtitleStyles)

const gridStyles = ({ theme }: Theme) => css`
  label: cashback__grid;
  display: grid;
  grid-template-columns: 1fr;
  gap: ${theme.spacings.mega};
  margin-top: 10px;

  ${theme.media.mega} {
    grid-template-columns: repeat(3, 1fr);
  }
`
export const Grid = styled('div')(gridStyles)

const baseCard = ({ theme }: Theme) => css`
  border-radius: 10px;
  padding: ${theme.spacings.mega};
  border: 2px solid ${theme.colors.n900};
`

const pointsCardStyles = ({ theme }: Theme) => css`
  label: cashback__points-card;
  ${baseCard({ theme })};
  background: ${theme.colors.white};
`
export const PointsCard = styled('div')(pointsCardStyles)

const cashbackCardStyles = ({ theme }: Theme) => css`
  label: cashback__cashback-card;
  ${baseCard({ theme })};
  background: ${theme.colors.white};
`
export const CashbackCard = styled('div')(cashbackCardStyles)

const cardHeaderStyles = ({ theme }: Theme) => css`
  label: cashback__card-header;
  display: flex;
  align-items: center;
  gap: ${theme.spacings.kilo};
`
export const CardHeader = styled('div')(cardHeaderStyles)

const smallLabelStyles = ({ theme }: Theme) => css`
  label: cashback__small-label;
  font-size: 12px;
  letter-spacing: 0.06em;
  font-weight: ${theme.font.weight.bold};
  color: ${theme.colors.n900};
`
export const SmallLabel = styled('div')(smallLabelStyles)

const amountStyles = ({ theme }: Theme) => css`
  label: cashback__amount;
  font-size: 32px;
  line-height: 1.1;
  font-weight: ${theme.font.weight.semibold};
  color: ${theme.colors.success};
  margin-top: ${theme.spacings.bit};

  strong {
    font-size: 24px;
  }
`
export const Amount = styled('div')(amountStyles)

const validStyles = ({ theme }: Theme) => css`
  label: cashback__valid-until;
  font-size: 12px;
  color: ${theme.colors.black};
  margin-top: ${theme.spacings.byte};
`
export const ValidUntil = styled('div')(validStyles)

const actionsStyles = ({ theme }: Theme) => css`
  label: cashback__actions;
  display: flex;
  gap: ${theme.spacings.kilo};
  margin-top: ${theme.spacings.giga};
  flex-wrap: nowrap;
  align-items: center;
  justify-content: center;
`
export const Actions = styled('div')(actionsStyles)

const primaryButtonStyles = ({ theme }: Theme) => css`
  label: cashback__primary-button;
  background: ${theme.colors.n900};
  color: ${theme.colors.white};
  border: 2px solid ${theme.colors.n900};
  border-radius: ${theme.borderRadius.mega};
  padding: ${theme.spacings.bit} ${theme.spacings.byte};
  font-size: 14px;
  font-weight: ${theme.font.weight.semibold};
  cursor: pointer;
  white-space: nowrap;
  transition:
    background 0.2s ease,
    border-color 0.2s ease,
    transform 0.05s ease;

  &:hover {
    background: ${theme.colors.n800};
    border-color: ${theme.colors.n800};
  }

  &:active {
    transform: translateY(1px);
  }

  &:disabled,
  &[aria-disabled='true'] {
    background: ${theme.colors.n100};
    border-color: ${theme.colors.n100};
    color: ${theme.colors.n400};
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  &:disabled:hover,
  &:disabled:active,
  &[aria-disabled='true']:hover,
  &[aria-disabled='true']:active {
    background: ${theme.colors.n100};
    border-color: ${theme.colors.n100};
    transform: none;
  }
`

export const PrimaryButton = styled('button')(primaryButtonStyles)

const secondaryButtonStyles = ({ theme }: Theme) => css`
  label: cashback__secondary-button;
  background: ${theme.colors.white};
  color: ${theme.colors.n900};
  border: 2px solid ${theme.colors.white};
  border-radius: ${theme.borderRadius.mega};
  padding: ${theme.spacings.bit};
  font-size: 14px;
  font-weight: ${theme.font.weight.bold};
  cursor: pointer;
  white-space: nowrap;
  transition:
    box-shadow 0.2s ease,
    transform 0.05s ease;
  box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.3);

  &:active {
    transform: translateY(1px);
  }
`
export const SecondaryButton = styled('button')(secondaryButtonStyles)

const textButtonStyles = ({ theme }: Theme) => css`
  label: cashback__text-button;
  background: transparent;
  border: 0;
  padding: 10px 8px;
  color: ${theme.colors.n900};
  font-size: 14px;
  font-weight: ${theme.font.weight.bold};
  cursor: pointer;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`
export const TextButton = styled('button')(textButtonStyles)

const iconBadgeStyles = ({ theme }: Theme) => css`
  label: cashback__icon-badge;
  width: ${theme.spacings.zetta};
  height: ${theme.spacings.zetta};
  min-width: ${theme.spacings.zetta};
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${theme.colors.n100};
`
export const IconBadge = styled('span')(iconBadgeStyles)

const shellLine = () => css`
  label: cashback__shell-line;
  width: 90px;
  height: 32px;
  border-radius: 4px;
`
export const ShellLine = styled('div')(shellLine)

const shellDoubleLine = () => css`
  label: cashback__shell-double-line;
  width: 140px;
  height: 16px;
  margin-top: 8px;
  border-radius: 4px;
`
export const ShellDoubleLine = styled('div')(shellDoubleLine)

const tolltip = ({ theme }: Theme) => css`
  label: cashback__tolltip;
  position: absolute;
  left: 50%;
  bottom: calc(100% + 10px);
  transform: translateX(-50%);
  padding: 10px 12px;
  border-radius: 8;
  background: ${theme.colors.n900};
  color: ${theme.colors.white};
  font-size: 12px;
  line-height: 16px;
  white-space: nowrap;
  box-shadow:
    0 8px 20px rgba(0, 0, 0, 0.18),
    0 2px 6px rgba(0, 0, 0, 0.12);
  z-index: 10;
`
export const Tolltip = styled('div')(tolltip)

const tolltipBullet = ({ theme }: Theme) => css`
  label: cashback__tolltip-bullet;
  position: absolute;
  left: 50%;
  top: 100%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 7px solid transparent;
  border-right: 7px solid transparent;
  border-top: 7px solid ${theme.colors.n900};
`
export const TolltipBullet = styled('div')(tolltipBullet)

const banner = ({ theme }: Theme) => css`
  background: ${theme.colors.g100};
  border-radius: 4px;
  padding: ${theme.spacings.mega};
  margin: ${theme.spacings.giga} ${theme.spacings.kilo};
  display: flex;
  gap: ${theme.spacings.byte};
  align-items: center;
  font-size: 12px;
  color: ${theme.colors.success};
  justify-content: space-between;
`

export const Banner = styled('div')(banner)

const bannerIcon = () => css`
  min-width: 35px;
  min-height: 24px;
`

export const BannerIcon = styled('div')(bannerIcon)

const button = ({ theme }: Theme) => css`
  background: ${theme.colors.success};
  border-radius: 4px;
  padding: ${theme.spacings.mega};
  font-size: 12px;
  color: ${theme.colors.white};
  width: 128px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  font-weight: bold;
`

export const Button = styled('div')(button)
